export type WorkshopCountUpdate = { seats: number; waitlistFull?: boolean };

// queue() and the apply() it defers to take the same arguments by design: the buffer sits
// transparently in front of the store's own updateWorkshopCount.
export type WorkshopCountWriter = (sessionId: number, seats: number, waitlistFull?: boolean) => void;

export type WorkshopCountBuffer = {
  queue: WorkshopCountWriter;
  flush: () => void;
  discard: (sessionId?: number) => void;
  pendingSize: () => number;
};

/**
 * Coalesces incoming `update-counts` SSE messages so the agenda repaints on a fixed cadence
 * instead of once per message.
 *
 * Every workshop write publishes a message to every connected client, so during registration
 * these arrive as a stream rather than as occasional events. Each payload is a FULL snapshot of
 * one workshop (seats + waitlist_full), never a delta, which is what makes coalescing lossless:
 * an intermediate message for a session can be dropped outright because the next one restates
 * everything it said.
 *
 * Leading-edge timer: the first message starts the window rather than extending it, so a
 * continuous stream still flushes every `flushMs` instead of being starved by debounce-style
 * resets that never settle.
 *
 * Deliberately free of Vue and Nuxt imports so the behaviour can be exercised on its own.
 */
export const createWorkshopCountBuffer = (
  apply: WorkshopCountWriter,
  flushMs = 250,
): WorkshopCountBuffer => {
  const pending = new Map<number, WorkshopCountUpdate>();
  let timer: ReturnType<typeof setTimeout> | null = null;

  const cancelTimer = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const flush = () => {
    // Closes the current window whether the timer fired or a caller flushed early, so the next
    // queued message opens a fresh one instead of inheriting a stale deadline.
    cancelTimer();
    if (pending.size === 0) return;

    // Copy before applying: `apply` writes into reactive state, and a watcher reacting
    // synchronously could otherwise queue into the map we are still iterating.
    const batch = [...pending.entries()];
    pending.clear();

    for (const [sessionId, update] of batch) {
      apply(sessionId, update.seats, update.waitlistFull);
    }
  };

  const queue = (sessionId: number, seats: number, waitlistFull?: boolean) => {
    // SSE payloads are parsed from the network and can be anything; a NaN would poison the
    // count for a workshop until the next reconciliation.
    if (!Number.isFinite(sessionId) || !Number.isFinite(seats)) return;

    pending.set(sessionId, { seats, waitlistFull });

    // Leading edge: only the FIRST message of a window arms the timer. Re-arming it on every
    // message would make this a debounce, and a continuous stream never lets a debounce settle.
    if (timer === null) {
      timer = setTimeout(flush, flushMs);
    }
  };

  /**
   * Drop buffered updates without applying them. Used when something authoritative or more
   * recent supersedes them: a server reconciliation, or the user's own optimistic update.
   */
  const discard = (sessionId?: number) => {
    if (sessionId !== undefined) {
      pending.delete(sessionId);
      return;
    }

    pending.clear();
    cancelTimer();
  };

  return { queue, flush, discard, pendingSize: () => pending.size };
};
